import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import { OptionType } from '@nxsys/components/AppSelectSync';
import AppTable from '@nxsys/components/AppTableContainer';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { revenueActions } from '@nxsys/store/slices/revenueFilings/reducer/revenueSlice';
import { PaginationParamsType } from '@nxsys/types/models/Common';
import {
  PSRPayloadType,
  PsrRecordType,
  PSRResponseType,
} from '@nxsys/types/models/revenueFilings/psr';
import { getCurrencySymbolByCountry, getTaxWeekTaxYear } from '@nxsys/utils';
import {
  handleDownloadJson,
  handleExportPSRPayroll,
  onExportPSRError,
} from '@nxsys/utils/psr';
import { showToast } from '@nxsys/utils/toast';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PSRHeader from '../../revenueFilings/Headers/PSRHeader';
import { getPSRColumns } from '../../revenueFilings/listing/PSR/columns';
import SelectDataTypeModal, {
  DownloadType,
} from '../../revenueFilings/modals/SelectDataTypeModal';
import { PSRFilterValues } from '../../revenueFilings/modals/PSRFilterModal';
import ViewJsonModal from '../../revenueFilings/modals/ViewJsonModal';

export type PSRFilterType = {
  taxYear: string;
  search: string;
  payFrequency: PayFrequencyValue | null;
  taxPeriod: number | null;
  companyRefs: OptionType[];
};

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

const PSRErrorSubmission: React.FC<Props> = ({
  cbAfterAction,
  isDashboard = false,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { payrollCountry, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { psrData, loading } = useSelector(
    (state: AppState) => state.revenueStore,
  );
  const { list, pagination } = psrData;

  const [filter, setFilter] = useState<PSRFilterType>({
    search: '',
    taxYear: getTaxWeekTaxYear().taxYear.toString(),
    payFrequency: null,
    taxPeriod: null,
    companyRefs: [],
  });

  const [isOpenDownloadTypeModal, setIsOpenDownloadTypeModal] = useState(false);
  const [isOpenViewTypeModal, setIsOpenViewTypeModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PsrRecordType | null>(
    null,
  );
  const [selectedRecordJson, setSelectedRecordJson] = useState<
    PSRPayloadType | PSRResponseType
  >();
  const [isViewJsonModalOpen, setIsViewJsonModalOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(filter.search || '', 500);

  const currencySymbol = getCurrencySymbolByCountry(payrollCountry);

  useEffect(() => {
    handleGetPSRRecords({
      search: debouncedSearchTerm,
      taxYear: filter.taxYear,
      payFrequency: filter.payFrequency,
      taxPeriod: filter.taxPeriod,
      companyRefs: filter.companyRefs,
      page: 0,
      size: 20,
    });
  }, [
    debouncedSearchTerm,
    filter.taxYear,
    filter.payFrequency,
    filter.taxPeriod,
    filter.companyRefs,
  ]);

  const handleGetPSRRecords = (
    params: PSRFilterType & PaginationParamsType,
  ) => {
    dispatch(
      revenueActions.getPsrRecords({
        search: params.search || undefined,
        taxYears: params.taxYear ? [params.taxYear] : [],
        payFrequencies: params.payFrequency ? [params.payFrequency] : [],
        taxPeriods: params.taxPeriod ? [params.taxPeriod.toString()] : [],
        companyIds: resolveFilterCompanyIds(
          params.companyRefs.map((ref) => ref.value || ''),
          isBureauEmployer,
          user?.companyUserIds,
        ),
        page: params.page || 0,
        size: params.size || 20,
        isSuccess: false,
      }),
    );
  };

  const onTaxYearChange = (value: number) => {
    setFilter({ ...filter, taxYear: value.toString() });
  };

  // Status is pinned to failed here, so the modal's status field is hidden
  const onApplyFilters = (values: PSRFilterValues) => {
    setFilter({
      ...filter,
      payFrequency: values.payFrequency,
      taxPeriod: values.taxPeriod,
      companyRefs: values.companyRefs,
    });
  };

  const onChangeSearchTerm = (searchTerm: string) => {
    setFilter({ ...filter, search: searchTerm });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetPSRRecords({
      ...filter,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleClickView = (record: PsrRecordType) => {
    setSelectedRecord(record);
    setIsOpenViewTypeModal(true);
  };

  const handleClickDownload = (record: PsrRecordType) => {
    setSelectedRecord(record);
    setIsOpenDownloadTypeModal(true);
  };

  const handleExportError = (record: PsrRecordType) => {
    onExportPSRError([record]);
  };

  const handleResubmit = async (record: PsrRecordType) => {
    if (!record?.leaverEmployment) {
      await dispatch(
        revenueActions.resubmitPsrRecords({
          ids: [record.id],
        }),
      ).unwrap();
    } else {
      await dispatch(
        revenueActions.resubmitLeaverPSRRecords({
          ids: [record.id],
        }),
      ).unwrap();
    }
    handleGetPSRRecords({
      ...filter,
      page: 0,
      size: 20,
    });
    cbAfterAction?.();
  };

  const handleExportErrorAll = async () => {
    const response = await dispatch(
      revenueActions.getPsrRecordsError({
        companyIds: resolveFilterCompanyIds(
          filter.companyRefs.map((ref) => ref.value || ''),
          isBureauEmployer,
          user?.companyUserIds,
        ),
        taxYears: filter.taxYear ? [filter.taxYear] : [],
        page: 0,
        size: pagination.totalDocs,
      }),
    ).unwrap();
    if (response.length === 0) {
      showToast({
        message: messages['revenue.psr.empty'],
        type: 'error',
      });
      return;
    }
    onExportPSRError(response);
  };

  const handleSelectDownload = (type: DownloadType) => {
    if (!selectedRecord) return;
    if (type === 'request') {
      handleDownloadJson(selectedRecord?.payload, type);
    } else if (type === 'response') {
      if (!selectedRecord?.response) {
        showToast({
          message: messages['common.noDataToDownload'],
          type: 'error',
        });
        return;
      }
      if (typeof selectedRecord?.response === 'string') {
        handleDownloadJson({ response: selectedRecord?.response }, type);
        return;
      }
      handleDownloadJson(selectedRecord?.response, type);
    } else {
      //payroll
      handleExportPSRPayroll([selectedRecord], currencySymbol);
    }
  };

  const handleSelectView = (type: DownloadType) => {
    setSelectedRecordJson(
      type === 'request' ? selectedRecord?.payload : selectedRecord?.response,
    );
    setIsViewJsonModalOpen(true);
  };

  const columns = getPSRColumns({
    handleClickView,
    handleClickDownload,
    handleExportError,
    handleResubmit,
    currencySymbol,
    messages,
  });

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          name={messages['common.exportErrorAll']}
          type="primary"
          variant="outlined"
          onClick={handleExportErrorAll}
        />
      </div>
    );
  };

  const renderContent = () => {
    return (
      <>
        <AppsHeader>
          <PSRHeader
            taxYear={filter.taxYear ? parseInt(filter.taxYear, 10) : 0}
            filters={{
              payFrequency: filter.payFrequency,
              taxPeriod: filter.taxPeriod,
              status: null,
              companyRefs: filter.companyRefs,
            }}
            setTaxYear={onTaxYearChange}
            onApplyFilters={onApplyFilters}
            onChangeSearchTerm={onChangeSearchTerm}
            searchTerm={filter.search}
          />
        </AppsHeader>
        <AppsContent>
          <AppTable
            data={list}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            columns={columns}
            titleEmpty={messages['revenue.psr.empty']}
            onChangePagination={onChangePagination}
          />
        </AppsContent>
        <SelectDataTypeModal
          isOpen={isOpenDownloadTypeModal}
          toggle={() => setIsOpenDownloadTypeModal(false)}
          onSelect={handleSelectDownload}
          showPayroll={true}
        />
        <SelectDataTypeModal
          isOpen={isOpenViewTypeModal}
          toggle={() => setIsOpenViewTypeModal(false)}
          onSelect={handleSelectView}
          title={messages['revenue.psr.viewType.title']}
          confirm={messages['revenue.psr.viewType.confirm']}
        />
        <ViewJsonModal
          isOpen={isViewJsonModalOpen}
          toggle={() => setIsViewJsonModalOpen(false)}
          data={selectedRecordJson || {}}
        />
      </>
    );
  };

  if (isDashboard) {
    return <>{renderContent()}</>;
  }

  return (
    <AppsContainer
      title="PSR Error Submissions"
      type="bottom"
      fullView
      rightContent={renderRightContent()}
    >
      <AppCard>{renderContent()}</AppCard>
    </AppsContainer>
  );
};

export default PSRErrorSubmission;
